import { useState } from 'react';

interface AMGPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AMGPopup({ isOpen, onClose }: AMGPopupProps) {
  const [showScheduler, setShowScheduler] = useState(false);

  if (!isOpen) return null;

  const handleFindYourAMG = () => {
    setShowScheduler(true);
  };

  const handleCloseScheduler = () => {
    setShowScheduler(false);
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 overflow-y-auto">
      <div className="min-h-screen px-6 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              AMG <span className="text-amber-400">Performance</span>
            </h2>
            <button
              onClick={onClose}
              className="w-12 h-12 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 rounded-full transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-white text-2xl"></i>
            </button>
          </div>

          <div className="bg-zinc-800 rounded-2xl overflow-hidden shadow-2xl mb-8">
            <div className="relative h-96 w-full overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/76eb0ea0c08fb34ca4023ab6358ed2c9.jpg"
                alt="AMG Performance"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent"></div>
            </div>
            
            <div className="p-8 md:p-12">
              <div className="mb-8">
                <h3 className="text-3xl font-bold text-white mb-4">
                  Experience AMG Excellence
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  Discover the pinnacle of Mercedes-Benz performance with our comprehensive AMG lineup. From the track-focused AMG GT to the versatile AMG SUVs, each vehicle represents the perfect fusion of luxury and raw power.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Hand-built engines, precision engineering, and race-inspired technology come together to deliver an unmatched driving experience. Whether you're seeking the thunderous V8 of the AMG GT 63 S or the refined power of an AMG E-Class, we have the perfect performance machine waiting for you.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-zinc-900 p-6 rounded-lg">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 rounded-lg mb-4">
                    <i className="ri-flashlight-line text-amber-400 text-2xl"></i>
                  </div>
                  <h4 className="text-white font-bold text-lg mb-2">Hand-Built Power</h4>
                  <p className="text-gray-400 text-sm">Every AMG engine crafted by master technicians</p>
                </div>
                <div className="bg-zinc-900 p-6 rounded-lg">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 rounded-lg mb-4">
                    <i className="ri-speed-line text-amber-400 text-2xl"></i>
                  </div>
                  <h4 className="text-white font-bold text-lg mb-2">Track Performance</h4>
                  <p className="text-gray-400 text-sm">Race-bred technology for the ultimate drive</p>
                </div>
                <div className="bg-zinc-900 p-6 rounded-lg">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 rounded-lg mb-4">
                    <i className="ri-trophy-line text-amber-400 text-2xl"></i>
                  </div>
                  <h4 className="text-white font-bold text-lg mb-2">Exclusive Access</h4>
                  <p className="text-gray-400 text-sm">Comprehensive selection of AMG models</p>
                </div>
              </div>

              <button 
                onClick={handleFindYourAMG}
                className="w-full bg-amber-400 text-black font-bold py-4 rounded-lg hover:bg-amber-500 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Find your next AMG!
              </button>
            </div>
          </div>
        </div>
      </div>

      {showScheduler && (
        <div className="fixed inset-0 bg-black/95 z-[60] overflow-y-auto">
          <div className="min-h-screen px-6 py-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-white">
                  Find Your <span className="text-amber-400">AMG</span>
                </h2>
                <button
                  onClick={handleCloseScheduler}
                  className="w-12 h-12 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-white text-2xl"></i>
                </button>
              </div>

              <div className="bg-zinc-900 rounded-lg shadow-2xl overflow-hidden">
                <div className="aspect-[16/9] w-full">
                  <iframe
                    src="https://mercedeswithjacob.square.site/"
                    className="w-full h-full"
                    style={{ minHeight: '600px' }}
                    title="Find Your AMG"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
